import { Edit2, Loader, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";

type Location = {
  id: number;
  name: string;
  type: "Entry" | "Exit";
  timestamp: string;
};

interface LocationManagementTabProps {
  selectedLocation: string;
  setSelectedLocation: (value: string) => void;
}

const LocationManagementTab: React.FC<LocationManagementTabProps> = ({
  selectedLocation,
  setSelectedLocation,
}) => {
  // State for locations
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for adding new locations
  const [newLocation, setNewLocation] = React.useState("");
  const [newLocationType, setNewLocationType] = React.useState<"Entry" | "Exit">("Entry");
  const [isAdding, setIsAdding] = useState(false);

  // State for editing
  const [editId, setEditId] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editType, setEditType] = React.useState<"Entry" | "Exit">("Entry");
  const [isEditing, setIsEditing] = useState(false);

  // Fetch all locations on component mount
  const fetchLocations = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("https://galaxy-alpr-be.onrender.com/api/locations");

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setLocations(data);

      // If no location is selected and we have locations, select the first one
      if (!selectedLocation && data.length > 0) {
        setSelectedLocation(data[0].name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch locations");
      console.error("Failed to fetch locations:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, setSelectedLocation]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleAddLocation = async () => {
    if (!newLocation.trim()) return;

    setIsAdding(true);
    setError(null);

    try {
      const response = await fetch("https://galaxy-alpr-be.onrender.com/api/locations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newLocation.trim(),
          type: newLocationType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error: ${response.status}`);
      }

      const data = await response.json();
      setLocations([...locations, data]);
      setNewLocation("");

      // Select newly added location if no location is currently selected
      if (!selectedLocation) {
        setSelectedLocation(data.name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add location");
      console.error("Failed to add location:", err);
    } finally {
      setIsAdding(false);
    }
  };

  const startEdit = (loc: Location) => {
    setEditId(loc.id);
    setEditName(loc.name);
    setEditType(loc.type);
  };

  const saveEdit = async (id: number) => {
    if (!editName.trim()) return;

    setIsEditing(true);
    setError(null);

    try {
      const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/locations/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: editName.trim(),
          type: editType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error: ${response.status}`);
      }

      const updatedLocation = await response.json();

      setLocations(
        locations.map(loc => loc.id === id ? updatedLocation : loc)
      );

      // Update selectedLocation if we're editing the selected one
      if (selectedLocation === locations.find(loc => loc.id === id)?.name) {
        setSelectedLocation(updatedLocation.name);
      }

      setEditId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update location");
      console.error("Failed to update location:", err);
    } finally {
      setIsEditing(false);
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const deleteLocation = async (id: number) => {
    setError(null);

    try {
      const response = await fetch(`https://galaxy-alpr-be.onrender.com/api/locations/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error: ${response.status}`);
      }

      const deletedLocation = locations.find(loc => loc.id === id);
      const updatedLocations = locations.filter(loc => loc.id !== id);
      setLocations(updatedLocations);

      // If we deleted the selected location, select another one if available
      if (deletedLocation && selectedLocation === deletedLocation.name) {
        setSelectedLocation(updatedLocations.length > 0 ? updatedLocations[0].name : "");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete location");
      console.error("Failed to delete location:", err);
    } finally {
      setConfirmDeleteId(null);
    }
  };

  if (loading && locations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="animate-spin mr-2" />
        <span>Loading locations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold mb-4">Add New Location</h2>
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter location name"
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
            disabled={isAdding}
          />
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="Entry"
                checked={newLocationType === "Entry"}
                onChange={() => setNewLocationType("Entry")}
                className="mr-2"
                disabled={isAdding}
              />
              Entry
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="Exit"
                checked={newLocationType === "Exit"}
                onChange={() => setNewLocationType("Exit")}
                className="mr-2"
                disabled={isAdding}
              />
              Exit
            </label>
          </div>
          <button
            onClick={handleAddLocation}
            disabled={!newLocation.trim() || isAdding}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAdding ? (
              <>
                <Loader className="inline animate-spin mr-2" size={16} />
                Adding...
              </>
            ) : "Add Location"}
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4">Existing Locations</h2>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Location Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {locations.length > 0 ? (
                locations.map((loc) => (
                  <tr
                    key={loc.id}
                    className={selectedLocation === loc.name ? "bg-blue-50" : ""}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editId === loc.id ? (
                        <input
                          type="text"
                          className="border border-gray-300 rounded px-2 py-1"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={isEditing}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => setSelectedLocation(loc.name)}
                        >
                          {loc.name}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editId === loc.id ? (
                        <select
                          value={editType}
                          onChange={(e) =>
                            setEditType(e.target.value as "Entry" | "Exit")
                          }
                          disabled={isEditing}
                          className="border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="Entry">Entry</option>
                          <option value="Exit">Exit</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${loc.type === "Entry" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                          }`}>
                          {loc.type}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-3">
                        {editId === loc.id ? (
                          <>
                            <button
                              onClick={() => saveEdit(loc.id)}
                              disabled={isEditing || !editName.trim()}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            >
                              {isEditing ? (
                                <Loader className="animate-spin" size={18} />
                              ) : "Save"}
                            </button>
                            <button
                              onClick={() => setEditId(null)}
                              disabled={isEditing}
                              className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(loc)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(loc.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                    No locations found. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {confirmDeleteId !== null && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
                <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
                <p className="mb-6">Are you sure you want to delete this location?</p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => confirmDeleteId !== null && deleteLocation(confirmDeleteId)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationManagementTab;
